import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { database } from "../firebaseConfig";
import { ref, get } from "firebase/database";
import ProductCard from "../components/ProductCard";
import SearchBar from "../components/SearchBar";
import { config } from "../config/config";
import "./Products.css";

const API_URL = config.baseUrl;

// Debounce function to limit resize event frequency
const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    };
};

const CategoryProducts = () => {
    const { name } = useParams();
    const navigate = useNavigate();
    const scrollContainerRef = useRef(null); // Ref to track scroll container

    const [allProducts, setAllProducts] = useState([]);
    const [category, setCategory] = useState(null);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const [showMoreLoading, setShowMoreLoading] = useState(false);
    const [cardsPerRow, setCardsPerRow] = useState(window.innerWidth <= 768 ? 2 : 5);
    const [visibleCount, setVisibleCount] = useState(5 * (window.innerWidth <= 768 ? 2 : 5));

    const searchActive = searchTerm.trim() !== "";
    const isAllProducts = name.toLowerCase() === "all";

    // Handle screen resize with debouncing
    useEffect(() => {
        const updateCardsPerRow = debounce(() => {
            const newCardsPerRow = window.innerWidth <= 768 ? 2 : 5;
            setCardsPerRow(newCardsPerRow);
            // Only increase visibleCount if more products should be shown
            setVisibleCount((prev) => {
                const minVisible = 5 * newCardsPerRow;
                return prev < minVisible ? minVisible : prev;
            });
        }, 200);

        window.addEventListener("resize", updateCardsPerRow);
        return () => window.removeEventListener("resize", updateCardsPerRow);
    }, []);

    // Fetch all products and current category
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const snapshot = await get(ref(database, "categories"));
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const categories = Array.isArray(data) ? data : Object.values(data);

                    let all = [];
                    let selected = null;

                    categories.forEach((cat) => {
                        if (!cat.products) return;

                        const prodArray = Array.isArray(cat.products)
                            ? cat.products
                            : Object.values(cat.products);

                        const enriched = prodArray.map((prod) => ({
                            ...prod,
                            __category: cat.category || "Unknown",
                        }));

                        all = all.concat(enriched);

                        if (
                            !isAllProducts &&
                            cat.category &&
                            cat.category.trim().toLowerCase() === name.trim().toLowerCase()
                        ) {
                            selected = cat;
                            setProducts(prodArray);
                        }
                    });

                    setAllProducts(all);
                    setCategory(isAllProducts ? { category: "All Products" } : selected);
                    setFilteredProducts(
                        isAllProducts ? all : selected?.products ? Object.values(selected.products) : []
                    );
                    // Reset visibleCount only on initial fetch or category change
                    setVisibleCount(5 * (window.innerWidth <= 768 ? 2 : 5));
                } else {
                    setAllProducts([]);
                    setCategory(null);
                    setFilteredProducts([]);
                    setVisibleCount(5 * (window.innerWidth <= 768 ? 2 : 5));
                }
            } catch (err) {
                console.error("Error fetching products:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [name, isAllProducts]);

    // Filter products on search
    useEffect(() => {
        const term = searchTerm.toLowerCase();
        if (searchActive) {
            const filtered = allProducts.filter(
                (prod) => prod.name?.toLowerCase().includes(term)
            );
            setFilteredProducts(filtered);
            // Reset visibleCount only when search term changes
            setVisibleCount(5 * cardsPerRow);
        } else {
            setFilteredProducts(isAllProducts ? allProducts : products);
        }
    }, [searchTerm, allProducts, products, isAllProducts, cardsPerRow]);

    const handleProductClick = (product) => {
        navigate("/product-details", { state: { product } });
    };

    const handleShowMore = () => {
        setShowMoreLoading(true);
        const scrollPosition = scrollContainerRef.current?.scrollTop || 0;

        setTimeout(() => {
            setVisibleCount((prev) => {
                const newCount = prev + 5 * cardsPerRow;
                console.log("Show More - New visibleCount:", newCount); // Debug log
                return newCount;
            });
            setShowMoreLoading(false);
            // Restore scroll position
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = scrollPosition;
            }
        }, 1000); // Simulate 1-second loading
    };

    const visibleProducts = filteredProducts.slice(0, visibleCount);
    const canShowMore = filteredProducts.length > visibleProducts.length;

    // Debug log for visibleCount changes
    useEffect(() => {
        console.log("visibleCount updated:", visibleCount, "filteredProducts length:", filteredProducts.length);
    }, [visibleCount, filteredProducts]);

    // Meta tags setup
    const pageTitle = searchActive
        ? "All Products - Marriott Pharmaceutical"
        : isAllProducts
            ? "All Products - Marriott Pharmaceutical"
            : category?.seo?.metaTitle || `${category?.category || ""} Products - Marriott Pharmaceutical`;

    const metaDescription = searchActive
        ? "Browse products from all categories."
        : isAllProducts
            ? "Browse all products from Marriott Pharmaceutical across all categories."
            : category?.seo?.metaDescription?.trim() ||
            category?.metaDescription?.trim() ||
            `Browse products under ${category?.category || ""}.`;

    const metaKeywords = searchActive
        ? "products, all categories"
        : isAllProducts
            ? "all products, Marriott Pharmaceutical"
            : category?.seo?.metaKeywords?.trim() || category?.category;

    if (loading) {
        return (
            <div className="loading-container" style={{ width: "100%" }}>
                <img
                    src={`${API_URL}uploads/loading.gif`}
                    alt="Loading..."
                    className="loading-gif"
                    style={{ width: "100%" }}
                />
            </div>
        );
    }

    if (!category && !searchActive && !isAllProducts) {
        return <div className="loading-message">Category not found.</div>;
    }

    return (
        <div className="our-products-page" ref={scrollContainerRef}>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={metaDescription} />
                <meta name="keywords" content={metaKeywords} />
            </Helmet>
            <button
                onClick={() => navigate(-1)}
                className="back-button"
                aria-label="Go back"
            >
                ←
            </button>
            <div className="header-container">
                <h1 className="our-products-title">
                    {searchActive ? "All Products" : `${category?.category || ""}`}
                </h1>
                <div className="search-bar-container">
                    <SearchBar
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        placeholder="Search products by name..."
                    />
                </div>
            </div>
            {filteredProducts.length > 0 ? (
                <>
                    <div
                        className="categories-grid"
                        style={{ gridTemplateColumns: `repeat(${cardsPerRow}, 1fr)` }}
                    >
                        {visibleProducts.map((prod, index) => (
                            <div
                                key={prod.id || index}
                                className="category-card-wrapper"
                                onClick={() => handleProductClick(prod)}
                                style={{ cursor: "pointer" }}
                            >
                                <ProductCard
                                    title={prod.name || ""}
                                    description={prod.description || ""}
                                    price={prod.sellingPrice || ""}
                                    mrp={prod.mrp || ""}
                                    discount={prod.discount || ""}
                                    rating={prod.rating || ""}
                                    mediaUrl={prod.image || "https://via.placeholder.com/300x200"}
                                    mediaType="image"
                                    edit={false}
                                    cardsPerRow={cardsPerRow}
                                    cardsPerRowPhoneView={2}
                                />
                            </div>
                        ))}
                    </div>
                    {canShowMore && (
                        <div style={{ textAlign: "center", marginTop: "20px" }}>
                            <button
                                onClick={handleShowMore}
                                className="show-more-btn"
                                style={{
                                    backgroundColor: "#8a2be2",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    padding: "10px 20px",
                                    cursor: showMoreLoading ? "not-allowed" : "pointer",
                                    fontSize: "16px",
                                    opacity: showMoreLoading ? 0.6 : 1,
                                }}
                                disabled={showMoreLoading}
                            >
                                {showMoreLoading ? "Loading..." : "Show More"}
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <p>No products match your search.</p>
            )}
        </div>
    );
};

export default CategoryProducts;